import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Save } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { czskAccountToIban, ibanToCzskAccount, isCzOrSkIban, looksLikeIban } from "@fohet/caravan-booker-shared/utils/iban"
import { inputClass, labelClass, sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"

interface FinancialFormValues {
    iban: string
    bic: string
    czskAccountNumber: string
    czskBankCode: string
    checkInTime: string
    checkOutTime: string
}

function initFinancialValues(areal: ArealResponse): FinancialFormValues {
    const stored = areal.bankAccount ?? ""
    let iban = ""
    let czskAccountNumber = ""
    let czskBankCode = ""

    if (stored) {
        if (isCzOrSkIban(stored)) {
            iban = stored
            const native = ibanToCzskAccount(stored) ?? ""
            const slashIdx = native.lastIndexOf("/")
            if (slashIdx !== -1) {
                czskAccountNumber = native.slice(0, slashIdx)
                czskBankCode = native.slice(slashIdx + 1)
            }
        } else if (looksLikeIban(stored)) {
            iban = stored
        } else {
            const slashIdx = stored.lastIndexOf("/")
            if (slashIdx !== -1) {
                czskAccountNumber = stored.slice(0, slashIdx)
                czskBankCode = stored.slice(slashIdx + 1)
            } else {
                czskAccountNumber = stored
            }
        }
    }

    return {
        iban,
        bic: areal.bic ?? "",
        czskAccountNumber,
        czskBankCode,
        checkInTime: areal.checkInTime ?? "",
        checkOutTime: areal.checkOutTime ?? "",
    }
}

function resolveIbanFromForm(
    values: FinancialFormValues,
    countryCode: "CZ" | "SK",
): { iban: string | undefined; error: string | null } {
    const ibanTrimmed = values.iban.trim()
    const accountTrimmed = values.czskAccountNumber.trim()
    const bankCodeTrimmed = values.czskBankCode.trim()

    if (accountTrimmed || bankCodeTrimmed) {
        const czskTrimmed = `${accountTrimmed}/${bankCodeTrimmed}`
        try {
            return { iban: czskAccountToIban(czskTrimmed, countryCode), error: null }
        } catch {
            return { iban: undefined, error: "invalid_czsk" }
        }
    }
    if (ibanTrimmed) return { iban: ibanTrimmed, error: null }
    return { iban: undefined, error: null }
}

export const FinancialSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const isCzSk = areal.country === "CZ" || areal.country === "SK"
    const countryCode = areal.country === "SK" ? "SK" : "CZ"

    const init = initFinancialValues(areal)
    const [iban, setIban] = useState(init.iban)
    const [bic, setBic] = useState(init.bic)
    const [accountNumber, setAccountNumber] = useState(init.czskAccountNumber)
    const [bankCode, setBankCode] = useState(init.czskBankCode)
    const [checkInTime, setCheckInTime] = useState(init.checkInTime)
    const [checkOutTime, setCheckOutTime] = useState(init.checkOutTime)
    const [accountError, setAccountError] = useState("")
    const [isPending, setIsPending] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setAccountError("")
        const values: FinancialFormValues = { iban, bic, czskAccountNumber: accountNumber, czskBankCode: bankCode, checkInTime, checkOutTime }
        const { iban: resolvedIban, error } = resolveIbanFromForm(values, countryCode)
        if (error) {
            setAccountError(t("admin.settings.ibanConvertError"))
            return
        }
        setIsPending(true)
        try {
            const updated = await api.patch<ArealResponse>(`/areals/${areal.id}`, {
                bankAccount: resolvedIban || undefined,
                bic: bic.trim() || undefined,
                checkInTime: checkInTime || undefined,
                checkOutTime: checkOutTime || undefined,
            })
            const next = initFinancialValues(updated)
            setIban(next.iban)
            setBic(next.bic)
            setAccountNumber(next.czskAccountNumber)
            setBankCode(next.czskBankCode)
            setCheckInTime(next.checkInTime)
            setCheckOutTime(next.checkOutTime)
            toast.success(t("admin.settings.toastSaved"))
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
        } catch (err) {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        } finally {
            setIsPending(false)
        }
    }

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionFinancial")}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className={labelClass}>{t("admin.settings.fieldIban")}</label>
                        <input
                            value={iban}
                            onChange={(e) => setIban(e.target.value)}
                            placeholder="CZ6508000000192000145399"
                            className={inputClass}
                        />
                        <p className="mt-1 text-xs text-slate-400">{t("admin.settings.fieldIbanHint")}</p>
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>{t("admin.settings.fieldBic")}</label>
                        <input
                            value={bic}
                            onChange={(e) => setBic(e.target.value)}
                            placeholder="FIOBCZPPXXX"
                            className={inputClass}
                        />
                        <p className="mt-1 text-xs text-slate-400">{t("admin.settings.fieldBicHint")}</p>
                    </div>
                    {isCzSk && (
                        <div className="col-span-2">
                            <label className={labelClass}>{t("admin.settings.fieldCzskAccount")}</label>
                            <div className="flex items-center gap-2">
                                <input
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value)}
                                    placeholder="123456789"
                                    className={cn(inputClass, "flex-1 w-auto min-w-0")}
                                />
                                <span className="text-slate-400 font-black text-lg select-none shrink-0">/</span>
                                <input
                                    value={bankCode}
                                    onChange={(e) => setBankCode(e.target.value)}
                                    placeholder="0800"
                                    maxLength={4}
                                    className={cn(inputClass, "w-24 shrink-0")}
                                />
                            </div>
                            {accountError && (
                                <p className="mt-1 text-xs text-red-500 font-bold">{accountError}</p>
                            )}
                            <p className="mt-1 text-xs text-slate-400">{t("admin.settings.fieldCzskAccountHint")}</p>
                        </div>
                    )}
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldCheckIn")}</label>
                        <input value={checkInTime} onChange={(e) => setCheckInTime(e.target.value)} type="time" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldCheckOut")}</label>
                        <input value={checkOutTime} onChange={(e) => setCheckOutTime(e.target.value)} type="time" className={inputClass} />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button type="submit" disabled={isPending} className={primaryBtnClass}>
                        <Save className="size-3.5" />
                        {isPending ? t("admin.settings.saving") : t("admin.settings.save")}
                    </button>
                </div>
            </form>
        </div>
    )
}
